<template>

<app-content :loading="is.loading">

	<app-content-head :title="title" :back="backPath" :readonly="$authorised('mem/subscriptions/access', 'read')">

		<app-content-head-actions-item v-if="!isNew" icon="tag" text="Tag" v-on:click="onTagClick" />
		<app-content-head-actions-item :divide="!isNew" :disabled="!isDirty" icon="undo" text="Undo" v-on:click="onUndoClick" />
		<app-content-head-actions-item :loading="is.saving" :disabled="!isDirty || !isValid" icon="save" text="Save" v-on:click="onSaveClick" />
		<app-content-head-actions-item v-if="!isNew && isExpiring" :divide="true" :disabled="isNew" icon="subscription.restore" text="Restore" v-on:click="onRestoreClick" />
		<app-content-head-actions-item v-if="!isNew && isActive" :divide="true" :disabled="isNew" :danger="true" icon="subscription.cancel" text="Cancel" v-on:click="onDeleteClick" />
		<app-content-head-actions-item v-if="!isNew && isCancelled" :divide="true" :disabled="isNew" :danger="true" icon="delete" text="Delete" v-on:click="onDeleteClick" />

	</app-content-head>

	<app-content-body v-if="!is.loading">

		<div class="summary-wrapper" v-if="!isNew">

			<app-content-summary :grow="true">

				<app-content-summary-item label="No." :value="item.reference" />
				<app-content-summary-item label="Ref." :value="item.referenceRandom" />
				<app-content-summary-item label="Type" :grow="true" :value="item.type.name" />
				<app-content-summary-item label="Plan" :value="item.plan.name" />
				<app-content-summary-item label="Started" :value="item.date.start | localDate('MM/DD/YYYY')" />
				<app-content-summary-item v-if="isActive" label="Next Renewal" :value="item.date.renew | localDate('MM/DD/YYYY')" />
				<app-content-summary-item v-if="!isActive" label="Cancelled on" :value="item.date.cancelled | localDate('MM/DD/YYYY')" />
				<app-content-summary-item v-if="isExpiring" label="Ends on" :value="item.date.renew | localDate('MM/DD/YYYY')" />
				<app-content-summary-item v-if="isCancelled" label="Ended" :value="item.date.renew | localDate('MM/DD/YYYY')" />

			</app-content-summary>

			<app-content-summary>

				<app-content-summary-item label="Total Checkins" :value="item.count.checkins" />
				<app-content-summary-item v-if="item.date.checkin" label="Last Checkin" :value="item.date.checkin | localDate('MM/DD/YYYY')" />
				<app-content-summary-item v-if="!item.date.checkin" label="Last Checkin" value="n/a" />

			</app-content-summary>

		</div>

        <app-content-box :readonly="$authorised('mem/subscriptions/access', 'read')">

			<div class="profile">

				<div class="profile-info">

					<app-input-account v-if="isNew" label="E-mail" :locked="!isNew" type="subscription" :text="emailMessages" v-model="model.email" v-on:exists="onExistsChange" :validation="$v.model.email" />
					<app-input-text v-if="!isNew" label="E-mail" :locked="true" v-model="model.email" validation="$v.model.email" notes="You cannot change an e-mail associated with a subscription as it is linked to a user account. The user must update their profile on the platform." />
					<app-input-text :locked="isCancelled" label="First name" v-model="model.firstname" :validation="$v.model.firstname" placeholder="Enter first name" :maxlength="32" />
					<app-input-text :locked="isCancelled" label="Last name" v-model="model.lastname" :validation="$v.model.lastname" placeholder="Enter last name" :maxlength="32" />
					
				</div>

				<div class="profile-photo">

					<div class="profile-photo-preview" :class="{'has-photo': model.photo}" v-bgimage="model.photo">

						<template v-if="!model.photo">No photo</template>

					</div>

					<app-input-image v-if="!isCancelled" :as-toolbar="true" :hide-preview="true" :allow-webcam="true" v-model="model.photo" :validation="$v.model.photo" />

				</div>

			</div>

		</app-content-box>

        <app-content-box v-if="isNew" :readonly="$authorised('mem/subscriptions/access', 'read')">

			<app-input-select v-if="references.plans.length" property="id" label="Plan" v-model="model.plan" :validation="$v.model.plan" placeholder="Select plan" :options="references.plans" />
			<app-input-select v-if="references.discounts.length" property="id" label="Discount" v-model="model.discount" :validation="$v.model.discount" placeholder="Select discount" :options="references.discounts" />
			<app-input-checklist :fullWidth="true" :singular="true" :columns="1" label="Method" v-model="model.billing.type" :validation="$v.model.billing.type" :options="$constants.membership.subscription.billing.typeName" :descriptions="$constants.membership.subscription.billing.typeDescription" />
			
		</app-content-box>

		<app-content-box title="Data Capture" icon="info" v-if="!isNew" :not-padded="true">

			<app-table :empty="!item.capture.length" emptyMessage="There is no data captured.">

				<app-table-head :layout="infoLayout">

					<app-table-column text="Name" />
					<app-table-column text="Answer" />

				</app-table-head>

				<app-table-body>

					<app-table-row :layout="infoLayout" v-for="(item, index) in item.capture" :key="index">

						<app-table-cell-text :text="item.label" />
						<app-table-cell-text :text="item.value" />

					</app-table-row>

				</app-table-body>

			</app-table>

		</app-content-box>

		<app-content-box title="Tags" icon="tags" v-if="!isNew" :not-padded="true">

			<app-table :empty="!references.tags.length" emptyMessage="There are no tags.">

				<app-table-head :layout="tagLayout">

					<app-table-column />
					<app-table-column text="Notes" />
					<app-table-column text="Date" />
					<app-table-column />

				</app-table-head>

				<app-table-body>

					<app-table-row :layout="tagLayout" v-for="tag in references.tags" :key="tag.id">

						<app-table-cell-colour :colour="tag.template.colour" />
						<app-table-cell-text :html="tagText(tag)" />
						<app-table-cell-text :text="tag.date | localDate('DD MMMM YYYY')" />
						<app-table-cell-action icon="edit" v-tooltip="'Edit tag'" v-on:click="onEditTag(tag)" />
						<app-table-cell-action icon="remove" theme="red" v-tooltip="'Remove tag'" :loading="is.removing === tag.id" v-on:click="onRemoveTag(tag)" />

					</app-table-row>

				</app-table-body>

			</app-table>

		</app-content-box>

		<app-content-box title="Billing" icon="billing" v-if="!isNew" :not-padded="true">

			<app-table :empty="!references.cycles.length" emptyMessage="There are no billing cycles.">

				<app-table-head :layout="cycleLayout">

					<app-table-column text="Status" />
					<app-table-column text="Date" />
					<app-table-column text="Type" />
					<app-table-column text="Plan" />
					<app-table-column text="Method" />
					<app-table-column text="Amount" />
					<app-table-column />

				</app-table-head>

				<app-table-body>

					<app-table-row :layout="cycleLayout" v-for="cycle in references.cycles" :key="cycle.id">

						<app-table-cell-label :text="$constants.membership.subscription.billing.statusName[cycle.status]" :type="$constants.membership.subscription.billing.statusColour[cycle.status]"  />
						<app-table-cell-text :text="cycle.date.start | localDate('MM/DD/YYYY')" />
						<app-table-cell-text :text="cycle.type.name" />
						<app-table-cell-text :text="cycle.plan.name" />
						<app-table-cell-text :text="$constants.membership.subscription.billing.typeName[cycle.method]" />
						<app-table-cell-text :text="cycle.amount | formatMoney" />
						<app-table-cell-action v-if="cycle.status === 1" icon="cycle.pay" v-tooltip="'Set as paid'" :loading="is.paying === cycle.id" v-on:click="onPayCycle(cycle)" />
						<app-table-cell-text v-if="cycle.status === 2"  />

					</app-table-row>

				</app-table-body>

			</app-table>

		</app-content-box>

	</app-content-body>

	<com-create v-if="is.tagging" :subscription="item.id" :tags="references.alltags" v-on:close="onTagAdded" />
	<com-edit v-if="is.editing" :subscription="item.id" :tag="is.editing" v-on:close="onTagEdited" />

</app-content>

</template>

<script>

import mixForm from '@/mixin/form'
import { required, maxLength, email } from 'vuelidate/lib/validators'

export default {

	mixins: [mixForm],

	components: {
		'com-create': () => import('./membershipmemberssubscription/AddTag'),
		'com-edit': () => import('./membershipmemberssubscription/EditTag')
	},

	data: function() {

		return {
			readonly: this.$authorised('mem/subscriptions/access', 'read'),
			is: {
				tagging: false,
				removing: false,
				editing: false
			},
			references: {
				cycles: [],
				tags: [],
				alltags: [],
				plans: [],
				discounts: []
            },
			infoLayout: '150px auto',
			tagLayout: '20px auto 160px 24px 24px',
			cycleLayout: '80px auto 160px 160px 80px 80px 24px',
			model: {
				id: false,
                firstname: '',
                lastname: '',
                email: '',
				photo: '',
				plan: false,
				discount: false,
				billing: {
					type: 0,
				}
			},
            emailMessages: {
				info: 'The e-mail address used must not already be associated with an existing subscription for this membership.',
				added: 'This e-mail address is already associated with a subscription.',
				exists: 'This e-mail address is already associated with an existing Cardboard Events account but they do not have a subscription.',
				register: 'A Cardboard Events login will be generated for this e-mail address and will be associated with their subscription. The login credentials will automatically be e-mailed to the user.'
			}
		}

	},

	validations: {
		model: {
            firstname: {
				required,
				maxLength: maxLength(32)
			},
			lastname: {
				required,
				maxLength: maxLength(32)
			},
            plan: {
				required
			},
            email: {
				required,
				email
			},
			photo: {
				required
			},
			billing: {}
		}
	},

	computed: {

		backPath: function() {

			if (this.isCancelled) {
			
				return 'Membership.Members.Cancelled'
			
			} else if (this.isExpiring) {
			
				return 'Membership.Members.Expiring'
			
			}

			return 'Membership.Members.Subscriptions'

		},

		isActive: function() {
		
			return this.item.status === 1
		
		},

		isCancelled: function() {
		
			return this.item.status === 2
		
		},

		isExpiring: function() {
		
			return this.item.status === 3
		
		}

	},

    methods: {

		onRestoreClick: function() {

			this.$router.push({
				name: 'Membership.Members.Subscription.Restore'
			})

		},

		tagText: function(tag) {

			var text = []

			text.push('<b>' + tag.template.name + '</b>')

			if (tag.template.text) text.push(' - ' + tag.template.text)

			if (tag.template.text && tag.text) text.push('<br/><br/>')
			
			if (!tag.template.text && tag.text) text.push(' - ')

			if (tag.text) text.push(tag.text)

			return text.join('')

		},

		onTagClick: function() {

			this.is.tagging = true

		},

		onTagAdded: async function(e) {

			e = e || {}
		
			if(e.added) await this.loadItem()

			this.is.tagging = false

		},

		onTagEdited: async function(e) {

			e = e || {}
		
			if(e.edited) await this.loadItem()

			this.is.editing = false

		},

		onPayCycle: function(cycle) {

			this.is.paying = cycle.id

			this.$api.patch(['membership/members/subscription', this.model.id, 'cycles', cycle.id]).then(function() {

				this.load()

				this.is.paying = false

			}.bind(this))

		},

		onRemoveTag: function(tag) {

			this.is.removing = tag.id

			this.$api.delete(['membership/members/subscription', this.model.id, 'tags', tag.id]).then(function() {

				this.load()

				this.is.removing = false

			}.bind(this))

		},

		onEditTag: function(tag) {

			this.is.editing = tag

		},
    
        onExistsChange: function(e) {

            if (e.exists) {

                this.model.firstname = e.firstname
                this.model.lastname = e.lastname

            }

		}
    
    }

}

</script>

<style scoped>

.profile {
	display: grid;
	grid-template-columns: auto 180px;
	grid-gap: 20px;
}

.profile-photo {
	width: 180px;
}

.profile-photo-preview {
	width: 180px;
	height: 180px;
	border: 2px solid #eeeeee;
	color: #ccc;
	line-height: 176px;
	text-align: center;
	font-size: 16px;
	border-radius: 10px;
	font-weight: 400;
	background-position: 50% 50%;
	background-size: contain;
	background-repeat: no-repeat;
}

.profile-photo-preview.has-photo {
	background-color: #000;
	border: 0px;
}

.profile-info {
}

.summary-wrapper {
	display: flex;
}

</style>